// AI Upgraded Component
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription, interval } from 'rxjs';
import { MeRepoService } from '@client/app/services/api/me-repo/me-repo.service';
import { TotalDomainsService } from '@client/app/services/total-domains/total-domains.service';
import { StateService } from '@uirouter/core';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { CoreDomainService } from '@client/app/core/services/domain/core-domain.service';
import { MonTableCollection } from '@monsido/ng2/models/table-collection.interface';
import { MonUIRouterTransitionService } from '@client/ng2/services/ui-router-transition-service/ui-router-transition-service';
import { DialogService } from '@monsido/angular-shared-components';

interface DownloadDialogParams {
    recipients: string[],
    category: string,
    category_ref: string,
    source_type: string,
    source_id: number,
    state: string,
    created_at: string,
    data_url: string,
}

@Component({
    selector: 'csv-exports-overlay',
    templateUrl: './csv-exports.component.html',
    standalone: false,
})
export class CsvExportsOverlayComponent implements OnInit, OnDestroy {
    domains: any[] = [];
    pages: MonTableCollection<DownloadDialogParams> = [];
    page: number = 1;
    pageSize: number = 10;
    subscriptions: Subscription = new Subscription();
    progress: boolean = false;
    constructor (
        private readonly meRepoService: MeRepoService,
        private readonly totalDomainsService: TotalDomainsService,
        private readonly stateService: StateService,
        private readonly monEventService: MonEventService,
        private readonly coreDomainService: CoreDomainService,
        private readonly monUIRouterTransitionService: MonUIRouterTransitionService,
        private readonly dialogService: DialogService,
    ) {}

    ngOnInit (): void {
        this.subscriptions.add(
            this.totalDomainsService.domainsEssential.subscribe((domains) => {
                this.domains = [];
                domains?.forEach((domain) => {
                    (domain as any).type = 'Domain';
                    domain.domain_groups?.forEach((domainGroup) => {
                        domainGroup.id = domain.id;
                        domainGroup.title = domain.title;
                        (domainGroup as any).url = domain.url;
                        (domainGroup as any).type = 'DomainGroup';
                        this.domains.push(domainGroup);
                    });
                    this.domains.push(domain);
                });
            }),
        );

        this.getPage();
        this.initialRefresh();
    }

    private initialRefresh (): void {
        const initialRefreshTimer = interval(10000).subscribe(() => {
            this.getPage();
            this.continuousRefresh();
            initialRefreshTimer.unsubscribe();
        });
    }

    private continuousRefresh (): void {
        this.subscriptions.add(
            interval(30000).subscribe(() => {
                this.getPage();
            }),
        );
    }

    private getPage (): void {
        const params = {
            page: this.page as unknown as string,
            page_size: this.pageSize as unknown as string,
        };
        this.progress = true;
        this.meRepoService.getExports(params).then(
            (data) => {
                const seoPrefix = 'seo_';
                (data as any[]).forEach((exportData) => {
                    const originalCategoryRef = exportData.category_ref;
                    const prefix = originalCategoryRef?.substring(0, seoPrefix.length);
                    let categoryRef = 'N/A';
                    if (prefix === seoPrefix) {
                        categoryRef = 'SEO ' + originalCategoryRef.substring(seoPrefix.length).replace(/_/g, ' ');
                    }
                    exportData.category_ref = categoryRef;
                });
                this.pages = data as any[];
            },
        ).catch((error) => {
            // eslint-disable-next-line no-console
            console.error('Error fetching exports:', error);
        }).finally(() => {
            this.progress = false;
        });
    }

    getDomainObjFromId (id: number, type: string): any {
        return (
            this.domains.find((domain) => type === domain.type && domain.id === id) ?? {
                url: '',
                domainTitle: '',
                title: '',
            }
        );
    }

    getTitle (domain: any): string {
        if (domain.type === 'DomainGroup') {
            return domain.domainTitle;
        } else if (domain.type === 'Domain') {
            return domain.title;
        } else {
            return 'Global';
        }
    }

    goToDomain (domain: any): void {
        if (!domain.id) {
            return;
        }

        const page = 'base.customer.domain.dashboard';
        let domainId = domain.id;

        if (domain.type === 'Domain') {
            this.coreDomainService.removeDomainGroup();
        } else {
            this.coreDomainService.setDomainGroup(domain);
            domainId = domain.domainId;
        }

        this.monUIRouterTransitionService.onTransitionFinished(() => this.stateService.go(page, { domainId: domainId }).then(() => {
            this.coreDomainService.changeDomain();
            this.dialogService.closeAll();
        }));
    }

    onPageChange (newPage) {
        this.page = newPage;
        this.monEventService.run('actionTrigger', { action: 'table-page-change', params: { page: newPage } });
        this.getPage();
    }

    onPageSizeChange (newPageSize) {
        this.pageSize = newPageSize;
        this.page = 1;
        this.monEventService.run('actionTrigger', { action: 'page-size-change', params: { pageSize: newPageSize } });
        this.getPage();
    }

    ngOnDestroy (): void {
        this.subscriptions.unsubscribe();
    }
}
